import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useReplyToFeedback } from '@/hooks/useFeedback'
import { ApiError } from '@/lib/api-error'
import { MAX_ATTACHMENTS, validateAttachments } from '@/lib/feedback/attachments'
import type { AttachmentError } from '@/lib/feedback/attachments'
import { AttachmentDropzone } from './AttachmentDropzone'

// Mirrors BODY_MAX_LENGTH in app/models/feedback.py.
const BODY_MAX_LENGTH = 10000

const ERROR_KEYS: Record<number, string> = {
  409: 'thread.errors.closed',
  413: 'errors.tooLarge',
  415: 'errors.unsupportedType',
  429: 'thread.errors.rateLimited',
}

function errorKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'thread.errors.generic'
  if (error.status === 422) {
    return /attachments/i.test(error.message) ? 'errors.tooMany' : 'thread.errors.invalid'
  }
  return ERROR_KEYS[error.status] ?? 'thread.errors.generic'
}

export function FeedbackReplyForm({ reportId }: { reportId: string }) {
  const { t } = useTranslation('feedback')
  const reply = useReplyToFeedback(reportId)
  const [body, setBody] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [fileErrors, setFileErrors] = useState<AttachmentError[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const sending = reply.isPending
  const canSubmit = body.trim() !== '' && !sending

  const addFiles = (incoming: File[]) => {
    const { accepted, errors } = validateAttachments(files, incoming, MAX_ATTACHMENTS)
    setFiles((current) => [...current, ...accepted])
    setFileErrors(errors)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitError(null)
    const form = new FormData()
    form.append('body', body.trim())
    for (const file of files) form.append('files', file)
    try {
      await reply.mutateAsync(form)
      setBody('')
      setFiles([])
      setFileErrors([])
    } catch (error) {
      setSubmitError(t(errorKey(error)))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 border-t pt-4">
      <div className="space-y-1">
        <Label htmlFor="feedback-reply">{t('thread.replyLabel')}</Label>
        <Textarea
          id="feedback-reply"
          rows={4}
          value={body}
          maxLength={BODY_MAX_LENGTH}
          placeholder={t('thread.replyPlaceholder')}
          onChange={(event) => setBody(event.target.value)}
        />
      </div>
      <AttachmentDropzone
        files={files}
        maxFiles={MAX_ATTACHMENTS}
        errors={fileErrors}
        onAdd={addFiles}
        onRemove={(index) => {
          setFiles((current) => current.filter((_, i) => i !== index))
          setFileErrors([])
        }}
      />
      {submitError && (
        <p role="alert" className="text-sm text-destructive">
          {submitError}
        </p>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={!canSubmit}>
          {sending ? t('thread.sending') : t('thread.send')}
        </Button>
      </div>
    </form>
  )
}
